import { useMemo, useState, type ReactNode } from "react";
import { Button } from "../ui/Button";
import { Select } from "../ui/Select";
import { TextField } from "../ui/TextField";
import styles from "../../styles/components/certification/ControlEvidenceBoard.module.css";

export type LinkedEvidenceItem = {
  id: string;
  title?: string | null;
  description?: string | null;
  fileName?: string | null;
  hasAttachment?: boolean;
  verificationStatus?: string | null;
  evidenceType?: string | null;
  artifactCategory?: string | null;
};

export type ControlEvidenceRow = {
  controlKey: string;
  title?: string | null;
  criteriaReference?: string | null;
  organizationSelfAssessment?: string | null;
  organizationReason?: string | null;
  auditorReviewStatus?: string | null;
  auditorComment?: string | null;
  linkedEvidenceCount?: number;
  linkedEvidence?: LinkedEvidenceItem[];
  openRequestCount?: number;
  findingCount?: number;
  preview?: boolean;
};

export type OrgAssessmentFilter = "ALL" | "COMPLIANT" | "PARTIAL" | "NON_COMPLIANT" | "UNASSESSED";
export type EvidencePresenceFilter = "ALL" | "WITH_EVIDENCE" | "WITHOUT_EVIDENCE";
export type AuditorReviewFilter =
  | "ALL"
  | "NOT_REVIEWED"
  | "ACCEPTED"
  | "IN_REVIEW"
  | "CLARIFICATION_REQUIRED"
  | "ADDITIONAL_EVIDENCE_REQUIRED"
  | "NON_CONFORMITY"
  | "HAS_OPEN_REQUEST"
  | "HAS_FINDING";

type Props = {
  mode: "organization" | "auditor";
  controls: ControlEvidenceRow[];
  emptyLabel: string;
  labels: {
    orgAssessment: string;
    auditorAssessment: string;
    reason: string;
    evidence: string;
    noEvidence: string;
    openRequests: string;
    findings: string;
    filters: string;
    filterAssessment: string;
    filterEvidence: string;
    filterReview: string;
    download: string;
    loading: string;
    save?: string;
    requestEvidence?: string;
    comment?: string;
  };
  formatOrgAssessment: (value?: string | null) => string;
  formatReviewStatus: (value?: string | null) => string;
  reviewStatusOptions?: Array<{ value: string; label: string }>;
  reviewDraft?: Record<string, string>;
  commentDraft?: Record<string, string>;
  onReviewStatusChange?: (controlKey: string, value: string) => void;
  onCommentChange?: (controlKey: string, value: string) => void;
  onSaveReview?: (controlKey: string) => void;
  onRequestEvidence?: (controlKey: string) => void;
  onDownloadEvidence?: (evidenceId: string, fileName?: string | null) => void;
  savingControlKey?: string | null;
  downloadingEvidenceId?: string | null;
  renderControlExtra?: (control: ControlEvidenceRow) => ReactNode;
};

export function normalizeOrgAssessment(value?: string | null): Exclude<OrgAssessmentFilter, "ALL"> {
  const raw = String(value || "").trim().toUpperCase();
  if (!raw || raw === "UNASSESSED" || raw === "PENDING" || raw === "N/A" || raw === "NA") return "UNASSESSED";
  if (["COMPLIANT", "YES", "IMPLEMENTED", "PASS", "FULLY_COMPLIANT", "COMPLETE"].includes(raw)) return "COMPLIANT";
  if (["PARTIAL", "PARTIAL_COMPLIANT", "IN_PROGRESS", "PARTIALLY_COMPLIANT"].includes(raw)) return "PARTIAL";
  if (["NON_COMPLIANT", "NO", "NOT_IMPLEMENTED", "FAIL", "NONCOMPLIANT"].includes(raw)) return "NON_COMPLIANT";
  if (raw.includes("PARTIAL")) return "PARTIAL";
  if (raw.includes("COMPLIANT") || raw === "YES") return "COMPLIANT";
  if (raw.includes("NON") || raw === "NO") return "NON_COMPLIANT";
  return "UNASSESSED";
}

function assessmentTone(value?: string | null): string {
  const norm = normalizeOrgAssessment(value);
  if (norm === "COMPLIANT") return styles.toneOk;
  if (norm === "PARTIAL") return styles.toneWarn;
  if (norm === "NON_COMPLIANT") return styles.toneBad;
  return styles.toneNeutral;
}

function reviewTone(value?: string | null): string {
  const raw = String(value || "NOT_REVIEWED").toUpperCase();
  if (raw === "ACCEPTED" || raw === "NOT_APPLICABLE_CONFIRMED") return styles.toneOk;
  if (raw === "CLARIFICATION_REQUIRED" || raw === "ADDITIONAL_EVIDENCE_REQUIRED" || raw === "IN_REVIEW") return styles.toneWarn;
  if (raw.includes("NON_CONFORMITY")) return styles.toneBad;
  return styles.toneNeutral;
}

export default function ControlEvidenceBoard({
  mode,
  controls,
  emptyLabel,
  labels,
  formatOrgAssessment,
  formatReviewStatus,
  reviewStatusOptions = [],
  reviewDraft = {},
  commentDraft = {},
  onReviewStatusChange,
  onCommentChange,
  onSaveReview,
  onRequestEvidence,
  onDownloadEvidence,
  savingControlKey = null,
  downloadingEvidenceId = null,
  renderControlExtra,
}: Props) {
  const [assessmentFilter, setAssessmentFilter] = useState<OrgAssessmentFilter>("ALL");
  const [evidenceFilter, setEvidenceFilter] = useState<EvidencePresenceFilter>("ALL");
  const [reviewFilter, setReviewFilter] = useState<AuditorReviewFilter>("ALL");

  const filtered = useMemo(() => {
    return (Array.isArray(controls) ? controls : []).filter((row) => {
      const assessment = normalizeOrgAssessment(row.organizationSelfAssessment);
      if (assessmentFilter !== "ALL" && assessment !== assessmentFilter) return false;

      const evidenceCount = Number(row.linkedEvidenceCount || row.linkedEvidence?.length || 0);
      if (evidenceFilter === "WITH_EVIDENCE" && evidenceCount <= 0) return false;
      if (evidenceFilter === "WITHOUT_EVIDENCE" && evidenceCount > 0) return false;

      if (mode === "auditor") {
        const review = String(row.auditorReviewStatus || "NOT_REVIEWED").toUpperCase();
        if (reviewFilter === "HAS_OPEN_REQUEST" && !(Number(row.openRequestCount || 0) > 0)) return false;
        if (reviewFilter === "HAS_FINDING" && !(Number(row.findingCount || 0) > 0)) return false;
        if (reviewFilter === "NON_CONFORMITY" && !review.includes("NON_CONFORMITY")) return false;
        if (
          reviewFilter !== "ALL"
          && reviewFilter !== "HAS_OPEN_REQUEST"
          && reviewFilter !== "HAS_FINDING"
          && reviewFilter !== "NON_CONFORMITY"
          && review !== reviewFilter
        ) {
          return false;
        }
      }
      return true;
    });
  }, [controls, assessmentFilter, evidenceFilter, reviewFilter, mode]);

  const assessmentOptions: Array<{ value: OrgAssessmentFilter; label: string }> = [
    { value: "ALL", label: "All assessments" },
    { value: "COMPLIANT", label: "Compliant / done" },
    { value: "PARTIAL", label: "Partial" },
    { value: "NON_COMPLIANT", label: "Not done" },
    { value: "UNASSESSED", label: "Unassessed" },
  ];
  const evidenceOptions: Array<{ value: EvidencePresenceFilter; label: string }> = [
    { value: "ALL", label: "All evidence states" },
    { value: "WITH_EVIDENCE", label: "With evidence" },
    { value: "WITHOUT_EVIDENCE", label: "Without evidence" },
  ];
  const reviewOptions: Array<{ value: AuditorReviewFilter; label: string }> = [
    { value: "ALL", label: "All auditor statuses" },
    { value: "NOT_REVIEWED", label: "Not reviewed" },
    { value: "IN_REVIEW", label: "In review" },
    { value: "ACCEPTED", label: "Accepted" },
    { value: "CLARIFICATION_REQUIRED", label: "Sent for rework" },
    { value: "ADDITIONAL_EVIDENCE_REQUIRED", label: "Additional info requested" },
    { value: "NON_CONFORMITY", label: "Non-conformity" },
    { value: "HAS_OPEN_REQUEST", label: "Has open evidence request" },
    { value: "HAS_FINDING", label: "Has findings" },
  ];

  return (
    <div className={styles.board}>
      <div className={styles.filters}>
        <p className={styles.filtersLabel}>{labels.filters}</p>
        <div className={styles.filterControls}>
          <Select
            label={labels.filterAssessment}
            value={assessmentFilter}
            onChange={(value) => setAssessmentFilter(String(value || "ALL") as OrgAssessmentFilter)}
            options={assessmentOptions}
          />
          <Select
            label={labels.filterEvidence}
            value={evidenceFilter}
            onChange={(value) => setEvidenceFilter(String(value || "ALL") as EvidencePresenceFilter)}
            options={evidenceOptions}
          />
          {mode === "auditor" ? (
            <Select
              label={labels.filterReview}
              value={reviewFilter}
              onChange={(value) => setReviewFilter(String(value || "ALL") as AuditorReviewFilter)}
              options={reviewOptions}
            />
          ) : null}
        </div>
        <p className={styles.resultCount}>
          {filtered.length} / {controls.length}
        </p>
      </div>

      <div className={styles.stack}>
        {filtered.map((ctrl) => {
          const evidenceItems = Array.isArray(ctrl.linkedEvidence) ? ctrl.linkedEvidence : [];
          return (
            <article key={ctrl.controlKey} className={styles.card}>
              <header className={styles.cardHead}>
                <div>
                  <h3 className={styles.cardTitle}>
                    {ctrl.controlKey}
                    {ctrl.title ? ` — ${ctrl.title}` : ""}
                  </h3>
                  {ctrl.criteriaReference ? <p className={styles.meta}>{ctrl.criteriaReference}</p> : null}
                </div>
                <div className={styles.badges}>
                  <span className={`${styles.badge} ${assessmentTone(ctrl.organizationSelfAssessment)}`}>
                    {labels.orgAssessment}: {formatOrgAssessment(ctrl.organizationSelfAssessment)}
                  </span>
                  <span className={`${styles.badge} ${reviewTone(ctrl.auditorReviewStatus)}`}>
                    {labels.auditorAssessment}: {formatReviewStatus(ctrl.auditorReviewStatus)}
                  </span>
                  <span className={`${styles.badge} ${evidenceItems.length ? styles.toneOk : styles.toneNeutral}`}>
                    {labels.evidence}: {evidenceItems.length}
                  </span>
                  {Number(ctrl.openRequestCount || 0) > 0 ? (
                    <span className={`${styles.badge} ${styles.toneWarn}`}>
                      {labels.openRequests}: {ctrl.openRequestCount}
                    </span>
                  ) : null}
                  {Number(ctrl.findingCount || 0) > 0 ? (
                    <span className={`${styles.badge} ${styles.toneBad}`}>
                      {labels.findings}: {ctrl.findingCount}
                    </span>
                  ) : null}
                </div>
              </header>

              <div className={styles.cardBody}>
                {ctrl.organizationReason ? (
                  <p className={styles.reason}>
                    <strong>{labels.reason}:</strong> {ctrl.organizationReason}
                  </p>
                ) : null}
                {ctrl.auditorComment ? (
                  <p className={styles.reason}>
                    <strong>{labels.auditorAssessment}:</strong> {ctrl.auditorComment}
                  </p>
                ) : null}

                <div className={styles.evidenceList}>
                  {evidenceItems.length ? (
                    evidenceItems.map((item) => (
                      <div key={item.id} className={styles.evidenceItem}>
                        <div>
                          <strong>{item.title || item.fileName || item.id}</strong>
                          <p className={styles.meta}>
                            {[item.evidenceType || item.artifactCategory, item.verificationStatus, item.fileName]
                              .filter(Boolean)
                              .join(" · ")}
                          </p>
                          {item.description ? <p className={styles.meta}>{item.description}</p> : null}
                        </div>
                        {item.hasAttachment && onDownloadEvidence ? (
                          <Button
                            type="button"
                            size="small"
                            color="secondary"
                            disabled={downloadingEvidenceId === item.id}
                            onClick={() => onDownloadEvidence(item.id, item.fileName)}
                          >
                            {downloadingEvidenceId === item.id
                              ? labels.loading
                              : `${labels.download}${item.fileName ? `: ${item.fileName}` : ""}`}
                          </Button>
                        ) : (
                          <span className={styles.meta}>{labels.noEvidence}</span>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className={styles.emptyEvidence}>{labels.noEvidence}</p>
                  )}
                </div>

                {mode === "auditor" ? (
                  <div className={styles.actions}>
                    <Select
                      label={labels.auditorAssessment}
                      value={reviewDraft[ctrl.controlKey] || ctrl.auditorReviewStatus || "NOT_REVIEWED"}
                      onChange={(value) => onReviewStatusChange?.(ctrl.controlKey, String(value || "NOT_REVIEWED"))}
                      options={reviewStatusOptions}
                    />
                    <TextField
                      label={labels.comment || "Comment"}
                      value={commentDraft[ctrl.controlKey] || ctrl.auditorComment || ""}
                      onChange={(value) => onCommentChange?.(ctrl.controlKey, value)}
                      placeholder={labels.comment || "Comment"}
                    />
                    <Button
                      type="button"
                      size="small"
                      color="primary"
                      disabled={savingControlKey === ctrl.controlKey}
                      onClick={() => onSaveReview?.(ctrl.controlKey)}
                    >
                      {savingControlKey === ctrl.controlKey ? labels.loading : labels.save || "Save"}
                    </Button>
                    <Button
                      type="button"
                      size="small"
                      color="secondary"
                      onClick={() => onRequestEvidence?.(ctrl.controlKey)}
                    >
                      {labels.requestEvidence || "Request Evidence"}
                    </Button>
                  </div>
                ) : null}

                {renderControlExtra?.(ctrl)}
              </div>
            </article>
          );
        })}
        {!filtered.length ? <p className={styles.empty}>{emptyLabel}</p> : null}
      </div>
    </div>
  );
}
